import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, MapPin, Home, DollarSign, TrendingUp, Users,
  CheckCircle, Circle, ChevronRight, ExternalLink, RefreshCw,
  Wrench, Building2, FileText, UserCheck, Send, MessageSquare,
  Zap, HandCoins, Crown, Shield, Star, Hash, StickyNote, Trash2, Pin,
  Activity, UserPlus, RefreshCcw, FileCheck, Flame, ShieldCheck
} from "lucide-react";
import NotarizationPanel from "./NotarizationPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { key: "draft",          label: "Draft",          color: "text-white/40",  bg: "bg-white/[0.06]",        dot: "bg-white/30" },
  { key: "live",           label: "Live",            color: "text-[#00FF88]", bg: "bg-emerald-500/10",      dot: "bg-[#00FF88]" },
  { key: "under_contract", label: "Under Contract",  color: "text-blue-400",  bg: "bg-blue-500/10",         dot: "bg-blue-400" },
  { key: "funded",         label: "Funded",          color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10",        dot: "bg-[#D4AF37]" },
  { key: "in_rehab",       label: "In Rehab",        color: "text-orange-400",bg: "bg-orange-500/10",       dot: "bg-orange-400" },
  { key: "closed",         label: "Closed",          color: "text-purple-400",bg: "bg-purple-500/10",       dot: "bg-purple-400" },
];

const ROLE_COLORS = {
  investor:   { label: "Investor",    color: "text-[#D4AF37]",   bg: "bg-[#D4AF37]/10",   border: "border-[#D4AF37]/20"  },
  lender:     { label: "Lender",      color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20"},
  wholesaler: { label: "Wholesaler",  color: "text-blue-400",    bg: "bg-blue-500/10",    border: "border-blue-500/20"   },
  contractor: { label: "Contractor",  color: "text-orange-400",  bg: "bg-orange-500/10",  border: "border-orange-500/20" },
  realtor:    { label: "Realtor",     color: "text-purple-400",  bg: "bg-purple-500/10",  border: "border-purple-500/20" },
  default:    { label: "Member",      color: "text-white/40",    bg: "bg-white/[0.04]",   border: "border-white/[0.06]"  },
};

function fmt(n) {
  if (!n) return "N/A";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function relTime(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function ParticipantAvatar({ picture, name, role, size = "sm" }) {
  const [err, setErr] = useState(false);
  const sizeClass = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  const roleCfg = ROLE_COLORS[role] || ROLE_COLORS.default;
  const initial = (name || "?")[0].toUpperCase();
  if (picture && !err) {
    const src = picture.startsWith("http") ? picture : `${BACKEND_URL}${picture}`;
    return <img src={src} alt={name} onError={() => setErr(true)} className={`${sizeClass} rounded-full object-cover border-2 ${roleCfg.border} flex-shrink-0`} />;
  }
  return (
    <div className={`${sizeClass} rounded-full ${roleCfg.bg} border-2 ${roleCfg.border} flex items-center justify-center font-bold ${roleCfg.color} flex-shrink-0`}>
      {initial}
    </div>
  );
}

export default function DealRoomPanel({ deal: initial, onClose, isOwner = false, currentUser }) {
  const [deal, setDeal] = useState(initial);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [isMember, setIsMember] = useState(false);
  const [joining, setJoining] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [capitalAmount, setCapitalAmount] = useState("");
  const [committingCapital, setCommittingCapital] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const currentStatus = STATUSES.find(s => s.key === deal?.deal_status) || STATUSES[1];
  const currentIdx    = STATUSES.findIndex(s => s.key === deal?.deal_status);
  const purchase = deal?.purchase_price || 0;
  const rehab    = deal?.rehab_estimate || 0;
  const arv      = deal?.arv || 0;
  const spread   = arv - purchase - rehab;
  const roi      = purchase + rehab > 0 ? ((spread / (purchase + rehab)) * 100).toFixed(1) : 0;

  useEffect(() => {
    fetchParticipants();
    fetchMessages();
    checkMembership();
    fetchNotes();
    fetchActivity();
  }, [deal?.deal_id]);

  useEffect(() => {
    if (activeTab === "discussion") {
      fetchMessages();
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
    if (activeTab === "participants") fetchParticipants();
    if (activeTab === "notes") fetchNotes();
    if (activeTab === "activity") fetchActivity();
  }, [activeTab]);

  const checkMembership = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/room-stats`, { withCredentials: true });
      setIsMember(res.data?.is_member || false);
    } catch {}
  };

  const fetchParticipants = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/participants`, { withCredentials: true });
      setParticipants(res.data?.participants || []);
    } catch {}
  };

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/messages`, { withCredentials: true });
      setMessages(res.data?.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  };

  const joinRoom = async () => {
    setJoining(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/join`, {}, { withCredentials: true });
      if (res.data.joined) {
        setIsMember(true);
        toast.success("Joined Deal Room! Auto-connected to deal creator.");
        fetchParticipants();
      } else {
        setIsMember(true);
        toast.info("You're already in this room");
      }
    } catch { toast.error("Failed to join"); }
    finally { setJoining(false); }
  };

  const sendMessage = async () => {
    if (!msgText.trim() || sending) return;
    if (!isMember) { toast.error("Join the Deal Room first"); return; }
    setSending(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/network/deals/${deal?.deal_id}/messages`,
        { content: msgText }, { withCredentials: true }
      );
      setMessages(prev => [...prev, res.data]);
      setMsgText("");
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch { toast.error("Failed to send message"); }
    finally { setSending(false); }
  };

  const updateStatus = async (statusKey) => {
    if (!isOwner) return;
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/deals/${deal.deal_id}/deal-status`,
        { deal_status: statusKey }, { withCredentials: true });
      setDeal(d => ({ ...d, deal_status: statusKey }));
      toast.success(`Status updated`);
    } catch { toast.error("Failed to update status"); }
    finally { setUpdating(false); }
  };

  const commitCapital = async () => {
    if (!capitalAmount) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/network/deals/${deal?.deal_id}/commit-capital`,
        { amount_k: Number(capitalAmount) },
        { withCredentials: true }
      );
      toast.success(`$${capitalAmount}K capital interest registered! Deal creator will contact you.`);
      setCapitalAmount("");
      setCommittingCapital(false);
      // Refresh activity feed
      fetchActivity();
    } catch {
      toast.error("Failed to register capital interest");
    }
  };

  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/notes`, { withCredentials: true });
      setNotes(res.data?.notes || []);
    } catch {}
  };

  const fetchActivity = async () => {
    setLoadingActivity(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/activity`, { withCredentials: true });
      setActivities(res.data?.activities || []);
    } catch {}
    finally { setLoadingActivity(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/network/deals/${deal?.deal_id}/notes`,
        { content: noteText }, { withCredentials: true }
      );
      setNotes(prev => [res.data, ...prev]);
      setNoteText("");
      toast.success("Note saved");
    } catch { toast.error("Failed to save note"); }
    finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/notes/${noteId}`, { withCredentials: true });
      setNotes(prev => prev.filter(n => n.note_id !== noteId));
    } catch { toast.error("Failed to delete note"); }
  };

  const togglePin = async (noteId, currentPinned) => {
    // Optimistic update
    setNotes(prev => {
      const updated = prev.map(n => n.note_id === noteId ? { ...n, pinned: !currentPinned } : n);
      return [...updated].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || (b.created_at > a.created_at ? 1 : -1));
    });
    try {
      await axios.patch(`${BACKEND_URL}/api/network/deals/${deal?.deal_id}/notes/${noteId}/pin`, {}, { withCredentials: true });
    } catch {
      // Revert on error
      setNotes(prev => prev.map(n => n.note_id === noteId ? { ...n, pinned: currentPinned } : n));
      toast.error("Failed to update pin");
    }
  };

  const TABS = [
    { key: "overview",      label: "Overview",    icon: Home },
    { key: "financials",    label: "Financials",  icon: DollarSign },
    { key: "activity",      label: "Activity",    icon: Activity,      badge: activities.length },
    { key: "discussion",    label: "Discussion",  icon: MessageSquare, badge: messages.length },
    { key: "notarize",      label: "Notarize",    icon: ShieldCheck },
    { key: "notes",         label: "My Notes",    icon: StickyNote,    badge: notes.length },
    { key: "participants",  label: "Team",         icon: Users,         badge: participants.length },
  ];

  const photo = deal?.photos?.[0];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/70 backdrop-blur-sm" />
      <div
        className="w-full sm:w-[520px] bg-[#060606] border-l border-white/[0.08] h-full flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="deal-room-panel"
      >
        {/* Hero Photo */}
        {photo && (
          <div className="relative h-32 overflow-hidden flex-shrink-0">
            <img src={photo.startsWith("http") ? photo : `${BACKEND_URL}${photo}`}
              alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#060606]" />
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-white/[0.06] flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${currentStatus.bg} ${currentStatus.color} border-current/20`}>
                <span className={`w-1.5 h-1.5 rounded-full ${currentStatus.dot} ${deal?.deal_status === 'live' ? 'animate-pulse' : ''}`} />
                {currentStatus.label}
              </span>
              {deal?.property_type && (
                <span className="text-[10px] text-white/30 border border-white/[0.06] px-2 py-0.5 rounded-full">{deal.property_type}</span>
              )}
              <span className="flex items-center gap-1 text-[10px] text-white/30">
                <Users className="w-3 h-3" /> {participants.length}
              </span>
            </div>
            <h2 className="text-white font-bold text-sm leading-tight flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
              <span className="truncate">{deal?.property_address}</span>
            </h2>
            <p className="text-white/30 text-xs mt-0.5">Posted by {deal?.operator_name}</p>
          </div>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {/* Join / Member */}
            {!isMember ? (
              <button onClick={joinRoom} disabled={joining}
                data-testid="join-deal-room-btn"
                className="px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-black font-bold text-xs rounded-full hover:from-[#C4A030] hover:to-[#B49020] disabled:opacity-50 transition-all">
                {joining ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Join Room"}
              </button>
            ) : (
              <span className="flex items-center gap-1 text-xs text-[#00FF88] bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full font-medium">
                <CheckCircle className="w-3 h-3" /> Member
              </span>
            )}
            <a href={`/deals/${deal?.deal_id}`} target="_blank" rel="noopener noreferrer"
              className="p-1.5 text-white/30 hover:text-[#D4AF37] transition-all">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={onClose} data-testid="close-deal-room"
              className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/[0.06] transition-all">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status Stepper */}
        <div className="px-5 py-3 border-b border-white/[0.06] flex-shrink-0">
          <div className="flex items-center justify-between">
            {STATUSES.map((s, i) => (
              <div key={s.key} className="flex items-center">
                <button onClick={() => isOwner && updateStatus(s.key)} disabled={!isOwner || updating}
                  title={isOwner ? `Set to ${s.label}` : s.label}
                  className={`flex flex-col items-center ${isOwner ? 'cursor-pointer' : 'cursor-default'}`}>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${i <= currentIdx ? `border-current ${s.dot} ${s.color}` : 'border-white/10 bg-transparent'}`}>
                    {i < currentIdx  && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                    {i === currentIdx && <span className={`w-2 h-2 rounded-full ${s.dot}`} />}
                    {i > currentIdx  && <Circle className="w-3 h-3 text-white/10" />}
                  </div>
                  <span className={`text-[9px] mt-1 ${i <= currentIdx ? s.color : 'text-white/20'}`}>{s.label}</span>
                </button>
                {i < STATUSES.length - 1 && (
                  <div className={`h-0.5 w-4 mx-0.5 mb-4 rounded-full transition-all ${i < currentIdx ? 'bg-white/30' : 'bg-white/[0.06]'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-white/[0.06] flex-shrink-0 overflow-x-auto scrollbar-none">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${
                  activeTab === tab.key ? 'text-white border-[#D4AF37]' : 'text-white/30 border-transparent hover:text-white'
                }`}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-0.5 text-[9px] font-black bg-[#D4AF37] text-black px-1.5 rounded-full">{tab.badge}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* OVERVIEW */}
          {activeTab === "overview" && (
            <div className="p-5 space-y-4">
              {deal?.marketplace_summary && (
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Deal Summary</p>
                  <p className="text-white/60 text-sm leading-relaxed">{deal.marketplace_summary}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "ARV",            value: fmt(arv),                         icon: TrendingUp, color: "text-[#00FF88]" },
                  { label: "Capital Needed", value: fmt(deal?.marketplace_capital_needed), icon: DollarSign, color: "text-[#D4AF37]" },
                  { label: "Purchase Price", value: fmt(purchase),                    icon: Home,       color: "text-blue-400" },
                  { label: "Rehab Budget",   value: fmt(rehab),                       icon: Wrench,     color: "text-orange-400" },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-3.5 h-3.5 ${color}`} />
                      <span className="text-[10px] text-white/30 uppercase tracking-wider">{label}</span>
                    </div>
                    <p className={`text-lg font-bold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
              {deal?.beds || deal?.baths || deal?.sqft ? (
                <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-3">Property Details</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    {deal?.beds  && <div><span className="text-white/30 text-xs">Beds</span><p className="text-white font-medium">{deal.beds}</p></div>}
                    {deal?.baths && <div><span className="text-white/30 text-xs">Baths</span><p className="text-white font-medium">{deal.baths}</p></div>}
                    {deal?.sqft  && <div><span className="text-white/30 text-xs">Sq Ft</span><p className="text-white font-medium">{deal.sqft?.toLocaleString()}</p></div>}
                  </div>
                </div>
              ) : null}
              {/* Capital Commitment CTA */}
              <div className="bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent border border-[#D4AF37]/20 rounded-xl p-4">
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-2 font-bold">💰 Interested in This Deal?</p>
                {committingCapital ? (
                  <div className="flex gap-2">
                    <input value={capitalAmount} onChange={e => setCapitalAmount(e.target.value)}
                      placeholder="Amount in $K (e.g. 50)"
                      className="flex-1 bg-black/40 border border-[#D4AF37]/20 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40" />
                    <button onClick={commitCapital} className="px-4 py-2 bg-[#D4AF37] text-black font-bold text-xs rounded-lg hover:bg-[#C4A030] transition-all">
                      Commit
                    </button>
                    <button onClick={() => setCommittingCapital(false)} className="p-2 text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => setCommittingCapital(true)}
                    className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2">
                    <HandCoins className="w-4 h-4" /> Commit Capital
                  </button>
                )}
              </div>
            </div>
          )}

          {/* FINANCIALS */}
          {activeTab === "financials" && (
            <div className="p-5 space-y-4">
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-5">
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-4">Deal Financials</p>
                {[
                  { label: "Purchase Price", value: fmt(purchase), color: "text-white" },
                  { label: "Rehab Budget",   value: fmt(rehab),    color: "text-orange-400" },
                  { label: "All-In Cost",    value: fmt(purchase + rehab), color: "text-white", bold: true },
                  { label: "After Repair Value", value: fmt(arv),  color: "text-[#00FF88]", bold: true },
                  { label: "Projected Spread",   value: fmt(spread), color: spread > 0 ? "text-[#D4AF37]" : "text-[#FF3B30]", bold: true },
                  { label: "Est. ROI", value: `${roi}%`, color: roi > 20 ? "text-[#00FF88]" : "text-[#D4AF37]", bold: true },
                ].map(({ label, value, color, bold }) => (
                  <div key={label} className="flex justify-between items-center border-b border-white/[0.04] py-2">
                    <span className="text-white/40 text-sm">{label}</span>
                    <span className={`text-sm ${bold ? 'font-bold' : ''} ${color}`}>{value}</span>
                  </div>
                ))}
              </div>
              {deal?.marketplace_strategy && (
                <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Exit Strategy</p>
                  <p className="text-white font-medium capitalize">{deal.marketplace_strategy.replace(/_/g, " ")}</p>
                </div>
              )}
            </div>
          )}

          {/* ACTIVITY LOG */}
          {activeTab === "activity" && (
            <div className="p-5 space-y-3" data-testid="activity-tab-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-white/30 uppercase tracking-wider">Deal Room Timeline</p>
                <button onClick={fetchActivity} disabled={loadingActivity}
                  className="p-1.5 text-white/20 hover:text-white/50 transition-all rounded-lg">
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingActivity ? "animate-spin" : ""}`} />
                </button>
              </div>
              {activities.length === 0 && !loadingActivity ? (
                <div className="text-center py-14">
                  <div className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
                    <Activity className="w-5 h-5 text-white/15" />
                  </div>
                  <p className="text-white/30 text-sm">No activity yet</p>
                  <p className="text-white/15 text-xs mt-1">Join the room to get started</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-white/[0.06]" />
                  <div className="space-y-3">
                    {activities.map((ev, i) => {
                      const type = ev.event_type || ev.auto_type || "update";
                      const iconCfg = {
                        joined:                   { icon: UserPlus,    color: "text-[#00FF88]",  bg: "bg-emerald-500/10",  border: "border-emerald-500/20" },
                        capital_committed:         { icon: HandCoins,   color: "text-[#D4AF37]",  bg: "bg-[#D4AF37]/10",   border: "border-[#D4AF37]/20"   },
                        marketplace_update:        { icon: RefreshCcw,  color: "text-blue-400",   bg: "bg-blue-500/10",    border: "border-blue-500/20"    },
                        document_shared:           { icon: FileCheck,   color: "text-purple-400", bg: "bg-purple-500/10",  border: "border-purple-500/20"  },
                        notarization_requested:    { icon: ShieldCheck, color: "text-amber-400",  bg: "bg-amber-500/10",   border: "border-amber-500/20"   },
                        notarized:                 { icon: ShieldCheck, color: "text-[#00FF88]",  bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
                        default:                   { icon: Activity,    color: "text-white/30",   bg: "bg-white/[0.04]",   border: "border-white/[0.06]"   },
                      }[type] || { icon: Activity, color: "text-white/30", bg: "bg-white/[0.04]", border: "border-white/[0.06]" };
                      const Icon = iconCfg.icon;
                      return (
                        <div key={ev.activity_id || i} className="flex gap-3 items-start pl-1">
                          <div className={`w-[30px] h-[30px] rounded-full ${iconCfg.bg} border ${iconCfg.border} flex items-center justify-center flex-shrink-0 z-10`}>
                            <Icon className={`w-3.5 h-3.5 ${iconCfg.color}`} />
                          </div>
                          <div className="flex-1 min-w-0 bg-white/[0.02] border border-white/[0.05] rounded-xl px-3 py-2.5">
                            <p className="text-white/70 text-xs leading-relaxed">{ev.content}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {ev.actor_name && (
                                <span className={`text-[10px] font-medium capitalize px-1.5 py-0.5 rounded-full ${iconCfg.bg} ${iconCfg.color}`}>
                                  {ev.actor_name}
                                </span>
                              )}
                              <span className="text-white/20 text-[10px]">{relTime(ev.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* NOTARIZE */}
          {activeTab === "notarize" && <NotarizationPanel deal={deal} />}

          {/* DISCUSSION */}
          {activeTab === "discussion" && (
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/[0.05] border border-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                      <MessageSquare className="w-5 h-5 text-[#D4AF37]/30" />
                    </div>
                    <p className="text-white/30 text-sm font-medium">No messages yet</p>
                    <p className="text-white/20 text-xs mt-1">Start the deal discussion</p>
                  </div>
                ) : (
                  messages.map(msg => {
                    const author = msg.author;
                    const name = author?.display_name || author?.name || "Member";
                    const pic  = author?.profile_picture_url;
                    const role = author?.user_type;
                    return (
                      <div key={msg.message_id} className="flex gap-3 group">
                        <ParticipantAvatar picture={pic} name={name} role={role} />
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-white/80 text-xs font-semibold">{name}</span>
                            <span className="text-white/20 text-[10px]">{relTime(msg.created_at)}</span>
                          </div>
                          <div className="mt-1 bg-white/[0.03] border border-white/[0.05] rounded-xl rounded-tl-sm px-3 py-2">
                            <p className="text-white/70 text-sm leading-relaxed">{msg.content}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              {/* Message input */}
              <div className="border-t border-white/[0.06] p-3">
                {!isMember ? (
                  <div className="flex items-center justify-between bg-[#D4AF37]/[0.05] border border-[#D4AF37]/15 rounded-xl px-4 py-3">
                    <p className="text-white/40 text-xs">Join the room to participate in discussions</p>
                    <button onClick={joinRoom} disabled={joining}
                      className="text-xs text-[#D4AF37] font-bold hover:text-[#C4A030] transition-all">
                      Join →
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input ref={inputRef} value={msgText} onChange={e => setMsgText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
                      placeholder="Ask a question, make an offer…"
                      className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/30"
                      data-testid="deal-room-message-input" />
                    <button onClick={sendMessage} disabled={sending || !msgText.trim()}
                      data-testid="send-deal-message-btn"
                      className="p-2.5 bg-[#D4AF37] hover:bg-[#C4A030] disabled:opacity-30 text-black rounded-xl transition-all">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PARTICIPANTS */}
          {activeTab === "participants" && (
            <div className="p-5 space-y-3">
              {participants.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
                    <Users className="w-5 h-5 text-white/15" />
                  </div>
                  <p className="text-white/30 text-sm">No participants yet</p>
                  <p className="text-white/20 text-xs mt-1">Be the first to join the room</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {participants.map((p, i) => {
                    const profile = p.profile || {};
                    const name = profile.display_name || profile.name || p.name || "Member";
                    const role = profile.user_type || p.role || "investor";
                    const roleCfg = ROLE_COLORS[role] || ROLE_COLORS.default;
                    return (
                      <div key={p.user_id} className="flex items-center gap-3 bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-3 hover:border-white/[0.1] transition-all">
                        <ParticipantAvatar picture={profile.profile_picture_url} name={name} role={role} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-semibold truncate">{name}</span>
                            {i === 0 && <Crown className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleCfg.bg} ${roleCfg.color} font-medium`}>{roleCfg.label}</span>
                            <span className="text-white/20 text-[10px]">{relTime(p.joined_at)}</span>
                          </div>
                        </div>
                        {profile.organization_name && (
                          <p className="text-white/25 text-xs truncate max-w-[100px]">{profile.organization_name}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {!isMember && (
                <button onClick={joinRoom} disabled={joining}
                  className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-black font-bold text-sm rounded-xl hover:from-[#C4A030] hover:to-[#B49020] disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                  <Users className="w-4 h-4" /> {joining ? "Joining…" : "Join Deal Room"}
                </button>
              )}
            </div>
          )}

          {/* PRIVATE NOTES */}
          {activeTab === "notes" && (
            <div className="flex flex-col h-full" data-testid="notes-tab-panel">
              {/* Compose */}
              <div className="p-4 border-b border-white/[0.06]">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <StickyNote className="w-3.5 h-3.5 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
                      placeholder="Add a private note — visible only to you…"
                      rows={3} data-testid="note-input"
                      className="w-full bg-yellow-500/[0.04] border border-yellow-500/15 rounded-xl px-3 py-2.5 text-sm text-white/80 placeholder:text-white/20 resize-none focus:outline-none focus:border-yellow-500/30 leading-relaxed" />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[9px] text-white/20">Private · ⌘↵ to save</p>
                      <button onClick={addNote} disabled={savingNote || !noteText.trim()}
                        data-testid="save-note-btn"
                        className="px-4 py-1.5 bg-yellow-500/90 hover:bg-yellow-400 disabled:opacity-30 text-black text-xs font-bold rounded-lg transition-all">
                        {savingNote ? "Saving…" : "Save Note"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {notes.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 rounded-2xl bg-yellow-500/[0.05] border border-yellow-500/10 flex items-center justify-center mx-auto mb-3">
                      <StickyNote className="w-5 h-5 text-yellow-400/25" />
                    </div>
                    <p className="text-white/30 text-sm font-medium">No notes yet</p>
                    <p className="text-white/15 text-xs mt-1">Your notes are private to you</p>
                  </div>
                ) : (
                  notes.map(note => (
                    <div key={note.note_id} data-testid={`note-${note.note_id}`}
                      className={`border rounded-xl p-4 group relative transition-all ${
                        note.pinned
                          ? "bg-[#D4AF37]/[0.06] border-[#D4AF37]/25 hover:border-[#D4AF37]/40"
                          : "bg-yellow-500/[0.04] border-yellow-500/[0.12] hover:border-yellow-500/25"
                      }`}>
                      {note.pinned && (
                        <div className="flex items-center gap-1 mb-2">
                          <Pin className="w-2.5 h-2.5 text-[#D4AF37] fill-[#D4AF37]" />
                          <span className="text-[9px] text-[#D4AF37] font-bold uppercase tracking-widest">Pinned</span>
                        </div>
                      )}
                      <p className="text-white/75 text-sm leading-relaxed whitespace-pre-line pr-12">{note.content}</p>
                      <p className="text-white/20 text-[10px] mt-2">{relTime(note.created_at)}</p>
                      {/* Action buttons — visible on hover */}
                      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                        <button onClick={() => togglePin(note.note_id, note.pinned)}
                          data-testid={`pin-note-${note.note_id}`}
                          title={note.pinned ? "Unpin" : "Pin to top"}
                          className={`p-1.5 rounded-lg transition-all ${note.pinned ? "text-[#D4AF37] bg-[#D4AF37]/10" : "text-white/20 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.07]"}`}>
                          <Pin className={`w-3 h-3 ${note.pinned ? "fill-[#D4AF37]" : ""}`} />
                        </button>
                        <button onClick={() => deleteNote(note.note_id)}
                          data-testid={`delete-note-${note.note_id}`}
                          className="p-1.5 text-white/20 hover:text-red-400 transition-all rounded-lg">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
